// src/components/admin/AdminHeader.tsx
"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "next/navigation";
import {
  Menu,
  RefreshCw,
  CheckCircle,
  Bell,
  ShoppingBag,
  AlertTriangle,
  CheckCheck,
  Clock,
  ArrowRight,
  Sun,
  Moon,
  Sliders,
  Palette,
  Globe,
  Check,
  ChevronDown,
} from "lucide-react";

// 🌟 ড্রয়ার এবং ডিজাইন স্টুডিও মডাল ইমপোর্ট
import StoreCustomizerDrawer from "@/components/admin/StoreCustomizerDrawer";
import SectionDesignModal from "@/components/admin/SectionDesignModal";

// 🌟 5 Supported Languages (1st Default: English, 2nd: Bangla, followed by Hindi, Urdu, Arabic)
type AdminLangCode = "en" | "bn" | "hi" | "ur" | "ar";

interface AdminLanguageOption {
  code: AdminLangCode;
  name: string;
  nativeName: string;
  badge: string;
  dir: "ltr" | "rtl";
}

const MERCHANT_LANGUAGES: AdminLanguageOption[] = [
  { code: "en", name: "English", nativeName: "English (Default)", badge: "EN", dir: "ltr" },
  { code: "bn", name: "Bangla", nativeName: "বাংলা (Bangla)", badge: "বাং", dir: "ltr" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी (Hindi)", badge: "हिं", dir: "ltr" },
  { code: "ur", name: "Urdu", nativeName: "اردو (Urdu)", badge: "اردو", dir: "rtl" },
  { code: "ar", name: "Arabic", nativeName: "العربية (Arabic)", badge: "عربي", dir: "rtl" },
];

interface AdminHeaderProps {
  activeTab: string;
  loading: boolean;
  onRefresh: () => void;
  onOpenMobileSidebar: () => void;
  subdomain?: string;
  onNavigateTab?: (tab: string) => void;
  theme?: "dark" | "light";
  onToggleTheme?: () => void;
}

export default function AdminHeader({
  activeTab,
  loading,
  onRefresh,
  onOpenMobileSidebar,
  subdomain: propSubdomain,
  onNavigateTab,
  theme: propTheme,
  onToggleTheme,
}: AdminHeaderProps) {
  const params = useParams();
  const subdomain = propSubdomain || (params?.subdomain as string) || "nextlook";

  // 🌟 Theme State (Dark mode is default)
  const [internalTheme, setInternalTheme] = useState<"dark" | "light">("dark");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = (localStorage.getItem("admin_theme") as "dark" | "light") || "dark";
      setInternalTheme(savedTheme);
      if (savedTheme === "light") {
        document.documentElement.classList.remove("dark");
        document.documentElement.classList.add("light");
      } else {
        document.documentElement.classList.remove("light");
        document.documentElement.classList.add("dark");
      }
    }
  }, []);

  const activeTheme = propTheme || internalTheme;

  const handleToggleTheme = () => {
    if (onToggleTheme) {
      onToggleTheme();
    } else {
      const nextTheme = activeTheme === "dark" ? "light" : "dark";
      setInternalTheme(nextTheme);
      if (typeof window !== "undefined") {
        localStorage.setItem("admin_theme", nextTheme);
        if (nextTheme === "light") {
          document.documentElement.classList.remove("dark");
          document.documentElement.classList.add("light");
        } else {
          document.documentElement.classList.remove("light");
          document.documentElement.classList.add("dark");
        }
      }
    }
  };

  // 🌟 Language Switcher States (Default: 'en' English)
  const [selectedLang, setSelectedLang] = useState<AdminLangCode>("en");
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const currentLangObj =
    MERCHANT_LANGUAGES.find((l) => l.code === selectedLang) || MERCHANT_LANGUAGES[0];

  const setTranslateCookie = useCallback((langCode: AdminLangCode) => {
    if (typeof document === "undefined") return;
    const cookieVal = `/auto/${langCode}`;
    document.cookie = `googtrans=${cookieVal}; path=/; max-age=31536000`;
    const host = window.location.hostname;
    if (host && !host.includes("localhost") && host !== "127.0.0.1") {
      document.cookie = `googtrans=${cookieVal}; path=/; domain=.${host}; max-age=31536000`;
    }
  }, []);

  const applyLanguageToDOM = useCallback(
    (langCode: AdminLangCode) => {
      if (typeof window === "undefined") return;

      setTranslateCookie(langCode);

      const selectEl = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
      if (selectEl) {
        selectEl.value = langCode;
        selectEl.dispatchEvent(new Event("change", { bubbles: true }));
      }
    },
    [setTranslateCookie]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const saved = (localStorage.getItem("merchant_admin_lang") as AdminLangCode) || "en";
    const validSaved = MERCHANT_LANGUAGES.some((l) => l.code === saved) ? saved : "en";
    setSelectedLang(validSaved);
    setTranslateCookie(validSaved);

    (window as any).googleTranslateElementInit = () => {
      if ((window as any).google && (window as any).google.translate) {
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: "auto",
            includedLanguages: "en,bn,hi,ur,ar",
            autoDisplay: false,
          },
          "xylo_hidden_translate_container"
        );

        setTimeout(() => {
          applyLanguageToDOM(validSaved);
        }, 400);
      }
    };

    if (!document.getElementById("xylo-neural-translate-script")) {
      const script = document.createElement("script");
      script.id = "xylo-neural-translate-script";
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        applyLanguageToDOM(validSaved);
      }, 300);
    }
  }, [applyLanguageToDOM, setTranslateCookie]);

  const handleSelectLanguage = (code: AdminLangCode) => {
    setSelectedLang(code);
    setIsLangMenuOpen(false);

    if (typeof window !== "undefined") {
      localStorage.setItem("merchant_admin_lang", code);
      applyLanguageToDOM(code);

      setTimeout(() => {
        applyLanguageToDOM(code);
      }, 250);
    }
  };

  // 🌟 ১. অন/অফ সেকশন ড্রয়ার স্টেট
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);

  // 🌟 ২. ডিজাইন ডেমো ও কোড ভিউয়ার মডাল স্টেট
  const [isDesignModalOpen, setIsDesignModalOpen] = useState(false);

  // Notification States
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isNotifOpen, setIsNotifOpen] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getTitle = () => {
    switch (activeTab) {
      case "overview":
        return "Overview Section";
      case "employees":
        return "Staff & Employees";
      case "attendance":
        return "Daily Attendance";
      case "payroll":
        return "Payroll & Salaries";
      case "departments":
        return "Departments & Roles";
      case "products":
        return "Products Section";
      case "inventory":
        return "Warehouse & Stock Manager";
      case "gallery":
        return "Media Gallery";
      case "categories":
        return "Categories Section";
      case "orders":
        return "Orders Fulfillment";
      case "coupons":
        return "Marketing Coupons";
      case "messages":
        return "Customer Live Chat";
      default:
        return "Store Customizer";
    }
  };

  const getApiUrl = useCallback(
    (endpoint: string) => {
      if (typeof window !== "undefined") {
        const host = window.location.hostname;
        if (host.includes("localhost") || host === "127.0.0.1") {
          return `http://${subdomain}.localhost:8000${endpoint}`;
        }
      }
      return endpoint;
    },
    [subdomain]
  );

  const getAuthToken = () => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/api/v1/stores/merchant/admin-notifications/"), {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setNotifications(data);
          const unread = data.filter((n) => !n.is_read).length;
          setUnreadCount(unread);
        }
      }
    } catch (e) {
      console.error("Header notification fetch error:", e);
    }
  }, [getApiUrl]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMarkAllAsRead = async () => {
    setNotifLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/api/v1/stores/merchant/admin-notifications/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (res.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleNotificationClick = (item: any) => {
    if (onNavigateTab) {
      onNavigateTab(item.link_tab || (item.type === "order" ? "orders" : "inventory"));
    }
    setIsNotifOpen(false);
  };

  return (
    <>
      {/* Hide external translation top banner & tooltips cleanly */}
      <style jsx global>{`
        .goog-te-banner-frame,
        .goog-te-Write-frame,
        .skiptranslate > iframe,
        #goog-gt-tt,
        .goog-te-balloon-frame,
        .goog-tooltip,
        .goog-tooltip:hover {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          width: 0 !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        .goog-text-highlight {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        body {
          top: 0px !important;
          position: static !important;
        }
        #xylo_hidden_translate_container {
          position: fixed;
          bottom: -9999px;
          left: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
          opacity: 0;
          pointer-events: none;
        }
      `}</style>

      {/* Hidden Neural Translation Mount Point */}
      <div id="xylo_hidden_translate_container" className="notranslate" aria-hidden="true" />

      <header
        className={`h-16 px-4 sm:px-8 border-b backdrop-blur-md flex items-center justify-between sticky top-0 z-30 font-sans transition-colors duration-200 ${
          activeTheme === "light"
            ? "bg-white/80 border-slate-200 text-slate-800"
            : "bg-slate-900/60 border-slate-800 text-white"
        }`}
      >
        <div className="flex items-center gap-3">
          {/* Mobile Hamburger Icon */}
          <button
            onClick={onOpenMobileSidebar}
            type="button"
            aria-label="Open Navigation Menu"
            className={`lg:hidden p-2 rounded-xl transition-colors cursor-pointer border ${
              activeTheme === "light"
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                : "bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700"
            }`}
          >
            <Menu className="w-5 h-5" />
          </button>

          <div>
            <h2
              className={`text-sm sm:text-base font-black capitalize ${
                activeTheme === "light" ? "text-slate-900" : "text-white"
              }`}
            >
              {getTitle()}
            </h2>
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-emerald-500 font-bold">
              <CheckCircle className="w-3 h-3" /> Verified Store Environment
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-2.5">
          {/* 🌟 ১. সেকশন অন/অফ সুইচ ড্রয়ার বাটন */}
          <button
            type="button"
            onClick={() => setIsCustomizerOpen(true)}
            className={`p-2 rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTheme === "light"
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200 shadow-sm"
                : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700"
            }`}
            title="Open Storefront Section Visibility Drawer"
            aria-label="Storefront Settings"
          >
            <Sliders className="w-4 h-4 text-emerald-500" />
            <span className="hidden xl:inline-block text-xs font-black">Sections</span>
          </button>

          {/* 🌟 ২. ডিজাইন ডেমো ও কোড ভিউয়ার বাটন (আইকনসহ) */}
          <button
            type="button"
            onClick={() => setIsDesignModalOpen(true)}
            className={`p-2 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-95 ${
              activeTheme === "light"
                ? "bg-amber-50 hover:bg-amber-100 text-amber-950 border-amber-300"
                : "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-amber-500/30"
            }`}
            title="Browse Design Demos & View Source Code"
            aria-label="Design Demos & Code"
          >
            <Palette className="w-4 h-4 text-amber-500" />
            <span className="hidden sm:inline-block text-xs font-black">Designs & Code</span>
          </button>

          {/* 🌟 ৩. মাল্টি-ল্যাঙ্গুয়েজ সুইচার বাটন (English Default, Bangla, Hindi, Urdu, Arabic) */}
          <div className="relative notranslate select-none" ref={langDropdownRef}>
            <button
              type="button"
              onClick={() => setIsLangMenuOpen((prev) => !prev)}
              className={`p-2 rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                activeTheme === "light"
                  ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200 shadow-sm"
                  : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700"
              }`}
              title="Change Merchant Dashboard Language"
              aria-label="Change Language"
            >
              <Globe className="w-4 h-4 text-emerald-500 shrink-0" />
              <span className="hidden md:inline-block text-xs font-black">
                {currentLangObj.name}
              </span>
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-500 text-[10px] font-black font-mono">
                {currentLangObj.badge}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                  isLangMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isLangMenuOpen && (
              <div
                className={`absolute right-0 mt-2 w-56 rounded-2xl border shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150 ${
                  activeTheme === "light"
                    ? "bg-white border-slate-200 text-slate-800"
                    : "bg-slate-900 border-slate-800 text-slate-100"
                }`}
              >
                <div className="px-3 py-1.5 border-b border-slate-200/60 dark:border-slate-800 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    Select Language
                  </span>
                  <span className="text-[9px] font-mono text-emerald-500 font-bold">
                    5 Languages
                  </span>
                </div>

                <div className="mt-1 space-y-0.5">
                  {MERCHANT_LANGUAGES.map((item) => {
                    const isActive = selectedLang === item.code;
                    return (
                      <button
                        key={item.code}
                        type="button"
                        onClick={() => handleSelectLanguage(item.code)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                          isActive
                            ? activeTheme === "light"
                              ? "bg-emerald-50 border-emerald-500/40 text-slate-950 font-black"
                              : "bg-emerald-500/15 border-emerald-500/40 text-white font-black"
                            : activeTheme === "light"
                            ? "border-transparent text-slate-700 hover:bg-slate-100"
                            : "border-transparent text-slate-300 hover:bg-slate-800/60"
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`w-8 h-6 rounded-lg flex items-center justify-center text-[10px] font-black font-mono ${
                              isActive
                                ? "bg-emerald-500 text-white"
                                : activeTheme === "light"
                                ? "bg-slate-100 text-slate-600"
                                : "bg-slate-800 text-slate-400"
                            }`}
                          >
                            {item.badge}
                          </span>
                          <div className="text-left leading-tight">
                            <div className="text-xs font-extrabold">{item.name}</div>
                            <div className="text-[10px] text-slate-400 font-medium">
                              {item.nativeName}
                            </div>
                          </div>
                        </div>

                        {isActive && (
                          <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 🌟 ৪. থিম টগল বাটন (ডার্ক/লাইট) */}
          <button
            type="button"
            onClick={handleToggleTheme}
            className={`p-2 rounded-xl border transition-all cursor-pointer ${
              activeTheme === "light"
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200 shadow-sm"
                : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700"
            }`}
            title={activeTheme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            aria-label="Toggle Theme"
          >
            {activeTheme === "dark" ? (
              <Sun className="w-4 h-4 text-amber-400 hover:rotate-90 transition-transform duration-300" />
            ) : (
              <Moon className="w-4 h-4 text-slate-700 hover:-rotate-12 transition-transform duration-300" />
            )}
          </button>

          {/* ৫. নোটিফিকেশন বেল ও ড্রপডাউন */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsNotifOpen((prev) => !prev)}
              className={`relative p-2 rounded-xl border transition-all cursor-pointer ${
                isNotifOpen
                  ? activeTheme === "light"
                    ? "bg-slate-200 border-emerald-500 text-slate-900"
                    : "bg-slate-800 border-emerald-500/50 text-white"
                  : activeTheme === "light"
                  ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                  : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700"
              }`}
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white shadow-md shadow-rose-500/50 animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {isNotifOpen && (
              <div
                className={`absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl border shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${
                  activeTheme === "light"
                    ? "bg-white border-slate-200 text-slate-800"
                    : "bg-slate-900 border-slate-800 text-white"
                }`}
              >
                <div
                  className={`p-3.5 border-b flex items-center justify-between ${
                    activeTheme === "light"
                      ? "bg-slate-50 border-slate-200"
                      : "bg-slate-950/70 border-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-amber-500" />
                    <span
                      className={`text-xs font-black ${
                        activeTheme === "light" ? "text-slate-900" : "text-white"
                      }`}
                    >
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-md bg-rose-500/10 text-rose-500 text-[10px] font-bold border border-rose-500/20">
                        {unreadCount} new
                      </span>
                    )}
                  </div>

                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllAsRead}
                      disabled={notifLoading}
                      className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-emerald-500 transition-colors cursor-pointer"
                    >
                      <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />
                      <span>Mark all read</span>
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto divide-y divide-slate-800/20 p-1.5">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-xs space-y-1">
                      <Bell className="w-6 h-6 mx-auto opacity-30 text-slate-400 mb-1.5" />
                      <p className="font-bold">No notifications yet</p>
                      <p className="text-[10px]">Order and stock alerts will appear here</p>
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((item) => {
                      const isOrder = item.type === "order";
                      const isLowStock = item.type === "low_stock";

                      return (
                        <div
                          key={item.id}
                          onClick={() => handleNotificationClick(item)}
                          className={`p-3 rounded-xl transition-all cursor-pointer flex items-start gap-2.5 my-1 ${
                            !item.is_read
                              ? activeTheme === "light"
                                ? "bg-slate-50 border border-emerald-500/30 hover:border-emerald-500"
                                : "bg-slate-950/90 border border-emerald-500/20 hover:border-emerald-500/40"
                              : activeTheme === "light"
                              ? "hover:bg-slate-100 border border-transparent"
                              : "bg-slate-900/60 hover:bg-slate-800/60 border border-transparent"
                          }`}
                        >
                          <div
                            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                              isOrder
                                ? "bg-emerald-500/20 text-emerald-500"
                                : isLowStock
                                ? "bg-amber-500/20 text-amber-500"
                                : "bg-blue-500/20 text-blue-500"
                            }`}
                          >
                            {isOrder ? (
                              <ShoppingBag className="w-3.5 h-3.5" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0 space-y-0.5">
                            <div className="flex items-center justify-between">
                              <h4
                                className={`text-[11px] font-bold truncate ${
                                  activeTheme === "light" ? "text-slate-900" : "text-white"
                                }`}
                              >
                                {item.title}
                              </h4>
                              {!item.is_read && (
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                              )}
                            </div>
                            <p
                              className={`text-[10px] leading-snug line-clamp-2 ${
                                activeTheme === "light" ? "text-slate-600" : "text-slate-300"
                              }`}
                            >
                              {item.message}
                            </p>
                            <div className="flex items-center justify-between text-[9px] text-slate-400 pt-1 font-mono">
                              <span className="flex items-center gap-1">
                                <Clock className="w-2.5 h-2.5" />
                                {item.created_at}
                              </span>
                              <span className="text-emerald-500 font-bold flex items-center gap-0.5">
                                View <ArrowRight className="w-2.5 h-2.5" />
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ৬. রিফ্রেশ বাটন */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`p-2 rounded-xl transition-all cursor-pointer border disabled:opacity-50 ${
              activeTheme === "light"
                ? "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"
                : "bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700"
            }`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-emerald-500" : ""}`} />
          </button>

          {/* ৭. অ্যাডমিন প্রোফাইল */}
          <div
            className={`flex items-center gap-2 pl-2 border-l ${
              activeTheme === "light" ? "border-slate-200" : "border-slate-800"
            }`}
          >
            <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-500 font-black flex items-center justify-center text-xs">
              M
            </div>
            <div className="hidden md:block text-left">
              <span
                className={`text-xs font-bold block leading-tight ${
                  activeTheme === "light" ? "text-slate-900" : "text-white"
                }`}
              >
                Merchant Owner
              </span>
              <span className="text-[10px] text-slate-400 uppercase font-mono">ADMIN</span>
            </div>
          </div>
        </div>
      </header>

      {/* 🌟 STORE CUSTOMIZER DRAWER */}
      <StoreCustomizerDrawer
        subdomain={subdomain}
        theme={activeTheme}
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
      />

      {/* 🌟 SECTION DESIGN GALLERY & CODE INSPECTOR MODAL */}
      <SectionDesignModal
        isOpen={isDesignModalOpen}
        onClose={() => setIsDesignModalOpen(false)}
        subdomain={subdomain}
        theme={activeTheme}
        primaryColor="#059669"
      />
    </>
  );
}